import React, { useState } from 'react';
import { MapPin, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LazyImage } from '@/components/ui/lazy-image';
import { Link } from 'react-router-dom';
import { usePublicBlogPosts } from '@/hooks/blog/usePublicBlogPosts';
import { Badge } from '@/components/ui/badge';
import { ReadingProgressBar } from '@/components/ui/reading-progress-bar';

// Featured destinations with dedicated hubs
const featuredDestinations = [
  {
    name: 'China',
    description: 'Ancient history, modern megacities, and landscapes beyond imagination',
    image: 'https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b?w=800',
    path: '/china',
    badge: 'Featured',
  },
];

export default function PublicDestinations() {
  const [currentPage, setCurrentPage] = useState(1);
  const { posts, loading, error } = usePublicBlogPosts({
    page: currentPage,
    limit: 9,
  });

  return (
    <div className="container py-8 md:py-12">
      <ReadingProgressBar isLoading={loading} />
      
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Discover Amazing Destinations</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Wonders Unfurled! Explore detailed guides and stories about incredible destinations around the world. 
          From hidden gems to popular spots, find inspiration for your next adventure.
        </p>
      </div>

      {/* Featured Destinations */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Featured Destinations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredDestinations.map((dest) => (
            <Link key={dest.path} to={dest.path}>
              <Card className="overflow-hidden group h-full hover:shadow-lg transition-shadow cursor-pointer">
                <div className="aspect-video relative overflow-hidden">
                  <LazyImage
                    src={dest.image}
                    alt={dest.name}
                    className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                    priority
                  />
                  {dest.badge && (
                    <Badge className="absolute top-3 right-3 bg-primary">{dest.badge}</Badge>
                  )}
                </div>
                <CardContent className="p-5">
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                    {dest.name}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3">{dest.description}</p>
                  <span className="inline-flex items-center text-primary font-medium">
                    Explore {dest.name} <ArrowRight className="ml-1 h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* All Destinations Header */}
      <h2 className="text-2xl font-bold mb-6">All Destination Guides</h2>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <div className="aspect-video">
                <div className="w-full h-full bg-muted animate-pulse"></div>
              </div>
              <CardContent className="p-5">
                <div className="h-6 bg-muted rounded animate-pulse mb-2"></div>
                <div className="h-4 bg-muted rounded animate-pulse w-3/4 mb-1"></div>
                <div className="h-4 bg-muted rounded animate-pulse w-1/2 mb-4"></div>
                <div className="h-8 bg-muted rounded animate-pulse w-1/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-10">
          <p>{error}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.length > 0 ? posts.map((post, index) => (
            <Card key={post.id} className="overflow-hidden group h-full hover:shadow-md transition-shadow">
              <div className="aspect-video relative overflow-hidden">
                <LazyImage
                  src={post.featured_image || 'https://images.unsplash.com/photo-1482938289607-e9573fc25ebb'}
                  alt={post.title || ''}
                  className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                  priority={index < 3} // First 3 items get priority loading
                  blurUpPreview={true}
                />
              </div>
              <CardContent className="p-5">
                <div className="flex items-start mb-2">
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {post.tags.map(tag => (
                        <Badge key={tag.id} variant="secondary">{tag.name}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <h2 className="text-xl font-semibold mb-2">{post.title}</h2>
                {post.excerpt && (
                  <p className="text-muted-foreground mt-2 line-clamp-2">{post.excerpt}</p>
                )}
                <Link to={`/blog/${post.slug}`} className="inline-flex items-center mt-4 text-primary hover:underline">
                  Read More <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
          )) : (
            <div className="col-span-full text-center py-10">
              <p className="text-muted-foreground">No destinations guides available yet.</p>
            </div>
          )}
        </div>
      )}

      <div className="text-center mt-10">
        <Button asChild variant="outline">
          <Link to="/blog">View All Travel Guides</Link>
        </Button>
      </div>
    </div>
  );
}
